import axios from 'axios';

// IMPORTANT: process.env.REACT_APP_API_URL is embedded at BUILD TIME
// It must be set before running `npm run build`
// In Docker: set via --build-arg REACT_APP_API_URL=https://...
// For development: set in .env file or use proxy (see package.json)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.tcg-api.eu/api/v1';

// Log the API URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('REACT_APP_API_URL from env:', process.env.REACT_APP_API_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies in requests
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
  verifyToken: () => api.get('/auth/verify-token'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const apiKeysAPI = {
  create: (keyData) => api.post('/api-keys', keyData),
  getAll: () => api.get('/api-keys'),
  update: (keyId, keyData) => api.put(`/api-keys/${keyId}`, keyData),
  delete: (keyId) => api.delete(`/api-keys/${keyId}`),
  toggle: (keyId) => api.post(`/api-keys/${keyId}/toggle`),
  getUsageStats: (days = 7) => api.get(`/api-keys/usage/stats?days=${days}`),
  getLogs: (keyId, params = {}) => api.get(`/api-keys/${keyId}/logs`, { params }),
};

export const tcgAPI = {
  getTcgList: () => api.get('/tcg'),
  // Batch endpoints for Home page (reduces N+1 API calls)
  getStatsBatch: () => api.get('/tcg/stats'),
  getSamplesBatch: (params = {}) => api.get('/tcg/samples', { params }),
  getSets: (tcg, params = {}) => api.get(`/tcg/${tcg}/sets`, { params }),
  getSet: (tcg, setId) => api.get(`/tcg/${tcg}/sets/${setId}`),
  getCards: (tcg, params = {}) => api.get(`/tcg/${tcg}/cards`, { params }),
  getCard: (tcg, cardId) => api.get(`/tcg/${tcg}/cards/${cardId}`),
  searchCards: (tcg, params = {}) => api.get(`/tcg/${tcg}/search`, { params }),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUserUsage: (userId, days = 30) => api.get(`/admin/users/${userId}/usage?days=${days}`),
  updateUserStatus: (userId, statusData) => api.put(`/admin/users/${userId}/status`, statusData),
  getApiKeys: (params = {}) => api.get('/admin/api-keys', { params }),
  blockIp: (ipData) => api.post('/admin/blocked-ips', ipData),
  unblockIp: (ipAddress) => api.delete(`/admin/blocked-ips/${ipAddress}`),
  getBlockedIps: (params = {}) => api.get('/admin/blocked-ips', { params }),
  getStats: (days = 7) => api.get(`/admin/stats?days=${days}`),
  getMissingImages: (params = {}) => api.get('/admin/missing-images', { params }),

  // Subscription management
  getSubscriptionTiers: () => api.get('/admin/subscription-tiers'),
  createSubscriptionTier: (tierData) => api.post('/admin/subscription-tiers', tierData),
  updateSubscriptionTier: (tierId, tierData) => api.put(`/admin/subscription-tiers/${tierId}`, tierData),
  updateUserSubscription: (userId, subscriptionData) => api.put(`/admin/users/${userId}/subscription`, subscriptionData),
  getUserSubscriptionHistory: (userId) => api.get(`/admin/users/${userId}/subscription-history`),

  // TCG configuration management
  getTcgConfigs: () => api.get('/admin/tcg-configs'),
  updateTcgConfig: (id, configData) => api.put(`/admin/tcg-configs/${id}`, configData),

    // TCG Data management
    getTcgSchema: (tcg) => api.get(`/admin/tcg-data/${tcg}/schema`),
    getTcgSets: (tcg, params = {}) => api.get(`/admin/tcg-data/${tcg}/sets`, {params}),
    getTcgSet: (tcg, setId) => api.get(`/admin/tcg-data/${tcg}/sets/${setId}`),
    createTcgSet: (tcg, data) => api.post(`/admin/tcg-data/${tcg}/sets`, data),
    updateTcgSet: (tcg, setId, data) => api.put(`/admin/tcg-data/${tcg}/sets/${setId}`, data),
    deleteTcgSet: (tcg, setId) => api.delete(`/admin/tcg-data/${tcg}/sets/${setId}`),
    getTcgCards: (tcg, setId, params = {}) => api.get(`/admin/tcg-data/${tcg}/sets/${setId}/cards`, {params}),
    getTcgCard: (tcg, cardId) => api.get(`/admin/tcg-data/${tcg}/cards/${cardId}`),
    createTcgCard: (tcg, data) => api.post(`/admin/tcg-data/${tcg}/cards`, data),
    updateTcgCard: (tcg, cardId, data) => api.put(`/admin/tcg-data/${tcg}/cards/${cardId}`, data),
    deleteTcgCard: (tcg, cardId) => api.delete(`/admin/tcg-data/${tcg}/cards/${cardId}`),
    uploadSetImage: (tcg, setId, formData) => api.post(`/admin/tcg-data/${tcg}/sets/${setId}/images`, formData, {headers: {'Content-Type': 'multipart/form-data'}}),
    uploadCardImage: (tcg, cardId, formData) => api.post(`/admin/tcg-data/${tcg}/cards/${cardId}/images`, formData, {headers: {'Content-Type': 'multipart/form-data'}}),
};

export const publicAPI = {
  health: () => axios.get(`${API_BASE_URL.replace('/api/v1', '')}/health/detailed`),
  getDocs: () => api.get('/docs'),
  getEndpoints: () => api.get('/docs/endpoints'),
  getExamples: () => api.get('/docs/examples'),
  getLastUpdated: (tcg) => api.get(`/tcg/${tcg}/last-updated`),
  getRequestStats: () => api.get('/stats/requests'),
};

export { API_BASE_URL };
export default api;